import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DateValidator } from 'ng2-validation/dist/date';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { DateHelpers } from '@edl-development/edl-common';
import { CplRequestItem } from '../../model/cpl-request-item.model';
import { CplUserRequest } from '../../model/cpl-user-request.model';
import { ReqVerificationResponse } from '../../model/req-verification-response.model';
import { UserRequestVerification } from '../../model/request/user-request-verification-action.model';
import { RequestHelpersService } from '../../services/request-helpers/request-helpers.service';
import { RequestVerifyService } from '../../services/request-verify.service';
import { RequestVerificationStatus } from '../enums/verification-status.enum';

@Component({
    templateUrl: './modify-due-date-modal.component.html',
    styleUrls: ['./modify-due-date-modal.component.scss']
})

export class ModifyDueDateModalComponent implements OnInit {
    @Input() requestItem: CplRequestItem;
    @Input() userRequest: CplUserRequest;
    @Input() userRequests:  CplUserRequest[];
    @Input() individualAction:Boolean;

    modifyDueDateForm: UntypedFormGroup;
    busy: boolean;
    // The toggle/note controls (and the notification itself) are only ever offered
    // once every request in this action has reached Follow-Up Stage — for a bulk
    // selection, all-or-nothing, not split per-request. Stage is judged from
    // UserStatus, not HasFollowup: the latter is true for a still-pending submission
    // whose item merely requires a follow-up (EDLP-3569).
    hasReachedFollowUpStage: boolean;


    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: UntypedFormBuilder,
        private requestVerifyService: RequestVerifyService,
        public requestHelpersService : RequestHelpersService,
        private toastr: ToastrService
    ) {}

    ngOnInit() {
        this.busy = false;
        let requests: CplUserRequest[];
        let MinDate: Date = DateHelpers.getMinDate();
        let EndDate: Date;
        let StartDate: Date;
        if (this.userRequests == undefined) {
            requests = [];
            requests.push(this.userRequest);
        }
        else {
            requests = this.userRequests;
        }

        if (requests.length == 1) {
            EndDate = new Date(requests[0].FollowupDueDate);
            EndDate = (EndDate > MinDate ? EndDate : null)
        }
        else {
            EndDate = null;
            StartDate = null;
        }

        this.hasReachedFollowUpStage = requests.every(request => this.requestHelpersService.hasFollowUpStageBegun(request.UserStatus));

        this.modifyDueDateForm = this.formBuilder.group({
            ModifyDueDate: [(EndDate), [DateValidator]],
            ModifyStartDate: [(StartDate), [DateValidator]],
            ShouldSendNotifications: [true],
            Note: ['']
        }, {validator: atLeastOne(Validators.required, ['ModifyDueDate', 'ModifyStartDate'])});
    }

    submit({ value, valid }: { value: any, valid: boolean}) {
        if (value.ModifyDueDate != null) {
            const dueDate = this.requestHelpersService.convertFollowupDueDateToLocal(value.ModifyDueDate);

            // No notification is ever sent before Follow-Up Stage has begun, regardless
            // of the (hidden, in that case) toggle's value.
            const shouldSendNotifications: boolean = this.hasReachedFollowUpStage && value.ShouldSendNotifications;
            const note: string = shouldSendNotifications ? value.Note : null;

            // Disables the Save & Close button (via [ladda]="busy") for the duration of
            // the request, preventing a double-click from firing duplicate submissions.
            this.busy = true;

            if (this.individualAction != undefined && this.individualAction) {
                const request = this.createRequest(this.userRequest, dueDate, shouldSendNotifications, note)

                this.requestVerifyService.verify(request)
                    .pipe(finalize(() => this.busy = false))
                    .subscribe((result) => {
                        this.toastr.success('Follow-Up due date successfully modified.');
                        this.activeModal.close();
                    });
            } else {
                const requests = this.userRequests.map(userRequest => this.createRequest(userRequest, dueDate, shouldSendNotifications, note))

                this.requestVerifyService.bulkVerify(requests)
                    .pipe(finalize(() => this.busy = false))
                    .subscribe((result: ReqVerificationResponse[]) => {
                        this.activeModal.close({ result });
                        return result;
                    }, (error) => {
                        console.log(error);
                    });
            }
        }
    }

    createRequest(userRequest: CplUserRequest, dueDate: Date, shouldSendNotifications: boolean, note: string) {
        const request = new UserRequestVerification();
        request.RefId = userRequest.UserRequestID;
        request.VerificationStatus = RequestVerificationStatus.FOLLOWUP_DUE_DATE_CHANGED;
        request.FollowupDueDate = dueDate;
        request.ShouldSendNotifications = shouldSendNotifications;
        request.UserNote = note;
        request.UserRequestCreatorFirstName = userRequest.FirstName;
        request.UserRequestCreatorLastName = userRequest.LastName;
        request.UserRequestDescription = userRequest.Description;

        return request;
    }

    cancel() {
        this.activeModal.dismiss();
    }
}

// `controlNames` restricts which controls count toward "at least one" — without it,
// adding ShouldSendNotifications (a boolean, always non-null, so always
// Validators.required-valid) to the group would silently satisfy this check on its
// own, defeating the date-fields check entirely.
export const atLeastOne = (validator: ValidatorFn, controlNames: string[] = null) => (
    group: UntypedFormGroup,
  ): ValidationErrors | null => {
    const keys = controlNames ?? (group?.controls ? Object.keys(group.controls) : []);
    const hasAtLeastOne =
      group &&
      group.controls &&
      keys.some(k => !validator(group.controls[k]));

    return hasAtLeastOne ? null : { atLeastOne: true };
  };